/**
 * Dashboard v2 hook — fetches /api/data/dashboard/v2 with Zod validation.
 * When CONFIG.DASHBOARD_V2_MOCK is true (or the endpoint 404s during rollout),
 * falls back to local fixtures so the UI is reviewable without the backend.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CONFIG } from '../config';
import { tokenManager } from '../services/api';
import {
  DashboardV2ResponseSchema,
  type DashboardV2Response,
  type DashboardV2Kpis,
  type DashboardV2TimeRange,
  type DashboardV2SbomTopRow,
  type DashboardV2RedTeamRow,
} from '../types';
import { DashboardV2KpisSchema } from '../schemas';
import { z } from 'zod';
import { SeveritySchema } from '../types';
import { getErrorMessage } from '../utils/errorHandler';
import { withJitter } from '../utils/polling';
import {
  buildDashboardV2Fixture,
  sbomTopFixture,
  redTeamFixture,
} from '../__fixtures__/dashboard-v2';

const DATA_SERVICE_URL = CONFIG.DATA_SERVICE_URL.replace(/\/+$/, '');

export interface DashboardV2Filters {
  timeRange: DashboardV2TimeRange;
  customFrom?: string | null;
  customTo?: string | null;
  applicationIds: string[];
}

export interface UseDashboardV2Result {
  data: DashboardV2Response | null;
  loading: boolean;
  /** True during a silent background refresh when stale data is already shown */
  isRefreshing: boolean;
  error: string | null;
  refresh: () => void;
  lastUpdated: string | null;
  /** True when time_range === 'live': no cache, no auto-refresh, manual only */
  isLive: boolean;
  /** KPI banner data available before the full response arrives */
  partialKpis: DashboardV2Kpis | null;
  /** True once KPI data is ready (even if full data is still loading) */
  kpisReady: boolean;
}

// ---------------------------------------------------------------------------
// localStorage cache (5 min) — keyed by filter values
// ---------------------------------------------------------------------------

// 5 minutes: reduces backend calls significantly; data is still fresh enough
const CACHE_TTL_MS = 5 * 60 * 1_000;
// Background auto-refresh cadence when tab is visible
const AUTO_REFRESH_MS = 5 * 60 * 1_000;
// Stop background retries after this many consecutive failures
const ERROR_BACKOFF_THRESHOLD = 3;

// Derive a stable string key from individual filter values instead of the
// filters object reference, so useMemo / useCallback dependencies are accurate.
// userId + tenantId are included so different users/tenants never share cached data.
const cacheKeyFor = (f: DashboardV2Filters, userId: string, tenantId: string) =>
  `nuguard_dashboard_v2_${tenantId}_${userId}_${f.timeRange}_${f.customFrom ?? ''}_${f.customTo ?? ''}_${[...f.applicationIds].sort().join(',')}`;

interface CacheEntry { data: DashboardV2Response; ts: number }

const readCache = (key: string): CacheEntry | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry;
    if (Date.now() - entry.ts > CACHE_TTL_MS) return null;
    return entry;
  } catch { return null; }
};
const writeCache = (key: string, data: DashboardV2Response) => {
  try { localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); }
  catch { /* quota; ignore */ }
};

export const clearDashboardV2Cache = () => {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith('nuguard_dashboard_v2_')) localStorage.removeItem(k);
    }
  } catch { /* ignore */ }
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDashboardV2(filters: DashboardV2Filters, userId = 'anon', tenantId = 'default'): UseDashboardV2Result {
  const [data, setData] = useState<DashboardV2Response | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [partialKpis, setPartialKpis] = useState<DashboardV2Kpis | null>(null);
  const [kpisReady, setKpisReady] = useState(false);
  const inflight = useRef(false);
  const consecutiveErrorsRef = useRef(0);
  const hasDataRef = useRef(false);
  const isLive = filters.timeRange === 'live';

  // Stable string key derived from individual filter values — not the object
  // reference. This prevents the load callback from being recreated on every
  // parent render, which would cause a continuous fetch loop.
  const appIdsKey = [...filters.applicationIds].sort().join(',');
  const key = useMemo(
    () => cacheKeyFor(filters, userId, tenantId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.timeRange, filters.customFrom, filters.customTo, appIdsKey, userId, tenantId],
  );

  // Keep a ref to the latest filter values so the load callback can read them
  // without adding `filters` to its dependency array.
  const filtersRef = useRef(filters);
  useEffect(() => { filtersRef.current = filters; });

  const load = useCallback(async (forceRefresh: boolean) => {
    if (inflight.current) return;

    if (!forceRefresh && !isLive) {
      const cached = readCache(key);
      if (cached) {
        setData(cached.data);
        setLastUpdated(new Date(cached.ts).toISOString());
        setLoading(false);
        setPartialKpis(null);
        setKpisReady(false);
        hasDataRef.current = true;
        return;
      }
    }

    inflight.current = true;

    // If we already have data, do a silent background refresh rather than
    // replacing the whole page with a skeleton.
    if (hasDataRef.current) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }

    const f = filtersRef.current;

    // Mock fallback gated on env flag
    if (CONFIG.DASHBOARD_V2_MOCK) {
      const range = f.timeRange === 'custom' ? '30d' : f.timeRange;
      const fixture = buildDashboardV2Fixture(range, f.applicationIds);
      const apps = fixture.applications;
      const merged: DashboardV2Response = { ...fixture, applications: apps, time_range: f.timeRange };
      setData(merged);
      writeCache(key, merged);
      setLastUpdated(new Date().toISOString());
      setLoading(false);
      setIsRefreshing(false);
      setError(null);
      consecutiveErrorsRef.current = 0;
      hasDataRef.current = true;
      inflight.current = false;
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append('time_range', f.timeRange);
      f.applicationIds.forEach(id => params.append('application_ids', id));
      if (f.customFrom) params.append('from', f.customFrom);
      if (f.customTo)   params.append('to', f.customTo);
      const rawOffset = new Date().getTimezoneOffset();
      const tzOffsetMinutes = Number.isFinite(rawOffset) ? -rawOffset : 0;
      params.append('tz_offset_minutes', String(tzOffsetMinutes));

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = tokenManager.getAccessToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Fire the lightweight /kpis endpoint in parallel with the full response.
      // It returns before the full response so KPI cards can render immediately.
      const kpiParams = new URLSearchParams();
      f.applicationIds.forEach(id => kpiParams.append('application_ids', id));
      const kpisFetch = fetch(
        `${DATA_SERVICE_URL}/dashboard/v2/kpis?${kpiParams.toString()}`,
        { headers },
      ).then(r => r.ok ? r.json() : null)
        .then(json => json ? DashboardV2KpisSchema.safeParse(json) : null)
        .then(result => {
          if (result?.success) {
            setPartialKpis(result.data);
            setKpisReady(true);
          }
        })
        .catch(() => { /* KPI endpoint failure is non-fatal */ });

      const [fullRes] = await Promise.all([
        fetch(`${DATA_SERVICE_URL}/dashboard/v2?${params.toString()}`, { headers }),
        kpisFetch,
      ]);
      if (!fullRes.ok) throw new Error(`HTTP ${fullRes.status}`);
      const json = await fullRes.json();
      const validated = DashboardV2ResponseSchema.parse(json);
      setData(validated);
      // Full data is now available — KPI partial state is superseded
      setPartialKpis(null);
      setKpisReady(false);
      if (!isLive) writeCache(key, validated);
      setLastUpdated(new Date().toISOString());
      setError(null); // clear prior error on success
      consecutiveErrorsRef.current = 0;
      hasDataRef.current = true;
    } catch (e) {
      consecutiveErrorsRef.current += 1;
      // Keep stale data visible; only update the error message
      setError(getErrorMessage(e, 'Failed to load dashboard'));
    } finally {
      setLoading(false);
      setIsRefreshing(false);
      inflight.current = false;
    }
  }, [key, isLive]); // key captures timeRange; isLive added to satisfy exhaustive-deps

  // Fetch on mount / filter change / manual tick
  useEffect(() => { load(false); }, [load, tick]);

  // Background auto-refresh: every AUTO_REFRESH_MS while the tab is visible.
  // Disabled in live mode — user must refresh manually to control cadence.
  // Backs off on repeated errors to avoid hammering the backend.
  useEffect(() => {
    if (isLive) return;
    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      if (consecutiveErrorsRef.current >= ERROR_BACKOFF_THRESHOLD) return;
      load(false);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') load(false);
    };
    const timerId = setInterval(tick, withJitter(AUTO_REFRESH_MS));
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(timerId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [load, isLive]);

  return {
    data,
    loading,
    isRefreshing,
    error,
    refresh: () => { if (!isLive) clearDashboardV2Cache(); setTick(n => n + 1); },
    lastUpdated,
    isLive,
    partialKpis,
    kpisReady,
  };
}

// ---------------------------------------------------------------------------
// Drill-down fetchers (lazy, called by drawers)
// ---------------------------------------------------------------------------

const SbomTopArraySchema = z.array(z.object({
  component: z.string(),
  version: z.string().nullable().optional(),
  cve: z.string().nullable().optional(),
  severity: SeveritySchema.nullable().optional(),
  node_type: z.string().nullable().optional(),
  cve_count: z.number().int().nonnegative().default(0),
}));

const PolicyViolationItemSchema = z.object({
  label: z.string(),
  count: z.number().int().nonnegative(),
  severity: z.string().default('MEDIUM'),
  framework: z.string().nullable().optional(),
  control_id: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
});
const PolicyViolationsResponseSchema = z.object({
  items: z.array(PolicyViolationItemSchema),
});

const RedTeamArraySchema = z.array(z.object({
  attack: z.string(),
  category: z.string(),
  severity: SeveritySchema,
  succeeded_at: z.string().nullable().optional(),
  source: z.string().default('Red-Team'),
}));

async function getJson(path: string) {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = tokenManager.getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${DATA_SERVICE_URL}${path}`, { headers });
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.json();
}

export async function fetchSbomTop(appId: string | null, limit = 20): Promise<DashboardV2SbomTopRow[]> {
  if (CONFIG.DASHBOARD_V2_MOCK) return sbomTopFixture;
  const params = new URLSearchParams({ limit: String(limit) });
  if (appId) params.append('application_id', appId);
  const json = await getJson(`/dashboard/v2/sbom-top?${params.toString()}`);
  return SbomTopArraySchema.parse(json).map(r => ({
    component: r.component,
    version: r.version ?? null,
    cve: r.cve ?? null,
    severity: r.severity ?? null,
    node_type: r.node_type ?? null,
    cve_count: r.cve_count ?? 0,
  }));
}

export type PolicyViolationItem = {
  label: string;
  count: number;
  severity: string;
  framework?: string | null;
  control_id?: string | null;
  description?: string | null;
};

export async function fetchPolicyViolations(appId: string | null): Promise<PolicyViolationItem[]> {
  if (CONFIG.DASHBOARD_V2_MOCK) {
    return [
      { label: 'LLM01: Prompt Injection',          count: 9,  severity: 'CRITICAL', framework: 'OWASP LLM Top 10', control_id: 'LLM01', description: 'Attackers manipulate LLMs via crafted inputs causing unintended actions or data exfiltration.' },
      { label: 'LLM02: Sensitive Info Disclosure', count: 12, severity: 'CRITICAL', framework: 'OWASP LLM Top 10', control_id: 'LLM02', description: 'LLMs may inadvertently reveal sensitive data including PII, credentials, or confidential business information.' },
      { label: 'LLM03: Excessive Agency',          count: 3,  severity: 'CRITICAL', framework: 'OWASP LLM Top 10', control_id: 'LLM03', description: 'LLM-based systems performing actions beyond intended scope due to excessive permissions or autonomy.' },
      { label: '6.4.1: Insecure Output Handling',  count: 2,  severity: 'MEDIUM',   framework: 'PCI-DSS v4',       control_id: '6.4.1', description: 'Application output is not adequately validated or sanitized before use.' },
      { label: 'AI-01: Data Poisoning Risk',       count: 1,  severity: 'HIGH',     framework: 'NIST AI RMF',      control_id: 'AI-01', description: 'Training or fine-tuning data may be manipulated to introduce malicious behaviors.' },
    ];
  }
  const params = new URLSearchParams();
  if (appId) params.append('application_id', appId);
  const json = await getJson(`/dashboard/v2/policy-violations?${params.toString()}`);
  return PolicyViolationsResponseSchema.parse(json).items;
}

export async function fetchRedTeamFindings(appId: string | null, limit = 20): Promise<DashboardV2RedTeamRow[]> {
  if (CONFIG.DASHBOARD_V2_MOCK) return redTeamFixture;
  const params = new URLSearchParams({ limit: String(limit) });
  if (appId) params.append('application_id', appId);
  const json = await getJson(`/dashboard/v2/redteam?${params.toString()}`);
  return RedTeamArraySchema.parse(json).map(r => ({
    attack: r.attack,
    category: r.category,
    severity: r.severity,
    succeeded_at: r.succeeded_at ?? null,
    source: r.source ?? 'Red-Team',
  }));
}

const CriticalFindingArraySchema = z.array(z.object({
  source: z.string(),
  title: z.string(),
  severity: z.string().default('CRITICAL'),
  recommended_fix: z.string().nullable().optional(),
  evidence: z.string().nullable().optional(),
  framework: z.string().nullable().optional(),
  occurred_at: z.string().nullable().optional(),
}));

export type CriticalFindingItem = {
  source: string;
  title: string;
  severity: string;
  recommended_fix?: string | null;
  evidence?: string | null;
  framework?: string | null;
  occurred_at?: string | null;
};

export async function fetchCriticalFindings(appId: string | null, limit = 50): Promise<CriticalFindingItem[]> {
  if (CONFIG.DASHBOARD_V2_MOCK) {
    return [
      { source: 'Policy', title: 'LLM02: Sensitive Info Disclosure', severity: 'CRITICAL', framework: 'OWASP LLM Top 10', recommended_fix: 'Add output filtering to redact PII and credentials before returning model responses.', evidence: 'LLMs may inadvertently reveal sensitive data including PII, credentials, or confidential business information.' },
      { source: 'Behavior', title: 'Unrestricted tool invocation', severity: 'CRITICAL', recommended_fix: 'Scope tool permissions to the minimum required for the task and require confirmation for destructive actions.', evidence: 'Agent invoked a file-deletion tool without user confirmation during a routine query.' },
      { source: 'Red-Team', title: 'Prompt injection via crafted document', severity: 'CRITICAL', recommended_fix: 'Sanitize untrusted document content before including it in the model context; treat retrieved content as data, not instructions.', evidence: 'Injected instructions in an uploaded PDF caused the model to exfiltrate the system prompt.' },
    ];
  }
  const params = new URLSearchParams({ limit: String(limit) });
  if (appId) params.append('application_id', appId);
  const json = await getJson(`/dashboard/v2/critical-findings?${params.toString()}`);
  return CriticalFindingArraySchema.parse(json);
}
